import Config from 'modules/config';
import Sentry from 'modules/sentry';

import { Logger, LoggerData } from './LoggerType';

/* eslint-disable no-console */

/*
 The idea being devLog vs activityLog is user actions/interactions are logged in
 activityLog, these may be useful beyond just dev t/s. For example, it would be easy
 to add a GA event for every activityLog thing created.

 On the other hand, devLog is stuff only useful to a dev. Most of the time we will treat these the sames, but
 perhaps not always so we leave them as separate streams
*/

enum LogLevels {
  silly = 'silly',
  debug = 'debug',
  verbose = 'verbose',
  info = 'info',
  warn = 'warn',
  error = 'error',
}

enum SentryBreadcrumbLevels {
  debug = 'debug',
  info = 'info',
  warning = 'warning',
  error = 'error',
  fatal = 'fatal',
}

interface LogEntry {
  level: LogLevels;
  message: string;
  data?: LoggerData;
}

interface LogFunc {
  (entry: LogEntry): void;
}

// The chrome dev console supports console.level calls for the below levels, use that func when available
const consoleSupportsLevel = (level: LogLevels): boolean =>
  [LogLevels.info, LogLevels.warn, LogLevels.error].includes(level);

// Sentry has minor differences in levels, so we need to convert
const transformNpmLevelToSentryLevel = (
  level: LogLevels,
): SentryBreadcrumbLevels => {
  if (level === LogLevels.info) {
    return SentryBreadcrumbLevels.info;
  }

  if (level === LogLevels.warn) {
    return SentryBreadcrumbLevels.warning;
  }

  if (level === LogLevels.error) {
    return SentryBreadcrumbLevels.error;
  }

  // For all other types, use lowest sentry priority
  return SentryBreadcrumbLevels.debug;
};

const logToConsole = (entry: LogEntry): void => {
  if (consoleSupportsLevel(entry.level)) {
    (console as any)[entry.level](entry.message, entry.data ? entry.data : '');
    return;
  }

  console.devLog.info(
    `${entry.level}: ${entry.message}`,
    entry.data ? entry.data : '',
  );
};

const sendToSentry = (category: string): LogFunc => (entry: LogEntry): void => {
  let stringData = '';
  try {
    stringData = JSON.stringify(entry.data);
  } catch (e) {
    console.devLog.debug('Error stringifyng breadcrumb data:', e);
  }

  Sentry.addBreadcrumb({
    level: transformNpmLevelToSentryLevel(entry.level),
    message: entry.message,
    category,
    data: { data: stringData },
  });
};

// This builds up the shorthand funcs so we can call log.info, log.warn, etc.
const createLogObject = (logFunc: LogFunc): Logger => ({
  log: (message: string, data?: LoggerData): void =>
    logFunc({ level: LogLevels.info, message, data }), // by default use info

  silly: (message: string, data?: LoggerData): void =>
    logFunc({ level: LogLevels.silly, message, data }),

  debug: (message: string, data?: LoggerData): void =>
    logFunc({ level: LogLevels.debug, message, data }),

  verbose: (message: string, data?: LoggerData): void =>
    logFunc({ level: LogLevels.verbose, message, data }),

  info: (message: string, data?: LoggerData): void =>
    logFunc({ level: LogLevels.info, message, data }),

  warn: (message: string, data?: LoggerData): void =>
    logFunc({ level: LogLevels.warn, message, data }),

  error: (message: string, data?: LoggerData): void =>
    logFunc({ level: LogLevels.error, message, data }),
});

// Actually construct and build the logging objects

let devLogFunc: LogFunc;
let activityLogFunc: LogFunc;

if (Config.ENV !== 'production') {
  devLogFunc = logToConsole;

  activityLogFunc = logToConsole;
} else {
  devLogFunc = sendToSentry('devLog');

  activityLogFunc = sendToSentry('activityLog');
}

const devLog = createLogObject(devLogFunc);
const activityLog = createLogObject(activityLogFunc);

export { devLog, activityLog };
